import { useState, useEffect, useCallback } from 'preact/hooks';
import { dataHub, type AdminUser } from '../lib/datahub';

const MIN_PW = 10;

// Owner-only accounts panel (full-screen overlay). Lists member accounts and lets the owner
// approve pending self-signups, revoke / reinstate access, provision a new member (temp
// password), or reset a member's password — all wired to the edge worker's /api/auth/admin/*
// endpoints (owner token sent automatically). Opened from the lock pill.
export function AdminPanel({ onClose }: { onClose: () => void }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Footer form: 'create' (provision a member) or a username being password-reset.
  const [mode, setMode] = useState<'create' | string>('create');
  const [formUser, setFormUser] = useState('');
  const [formPass, setFormPass] = useState('');
  const [formMsg, setFormMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    dataHub
      .listUsers()
      .then((r) => setUsers(r.users || []))
      .catch((e) => {
        setError((e as Error).message);
        setUsers([]);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (username: string, fn: (u: string) => Promise<unknown>) => {
    setBusy(username);
    setError(null);
    try {
      await fn(username);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const resetForm = () => {
    setMode('create');
    setFormUser('');
    setFormPass('');
    setFormMsg(null);
  };

  const submitForm = async (e: Event) => {
    e.preventDefault();
    setFormMsg(null);
    if (formPass.length < MIN_PW) {
      setFormMsg(`Temp password needs at least ${MIN_PW} characters.`);
      return;
    }
    if (mode === 'create') {
      const u = formUser.trim().toLowerCase();
      if (!/^[a-z0-9._-]{3,32}$/.test(u)) {
        setFormMsg('Username must be 3-32 chars: a-z 0-9 . _ -');
        return;
      }
      if (u === 'owner') {
        setFormMsg('That username is reserved.');
        return;
      }
    }
    setBusy('__form');
    try {
      if (mode === 'create') await dataHub.createUser(formUser.trim().toLowerCase(), formPass);
      else await dataHub.resetUserPassword(mode, formPass);
      resetForm();
      load();
    } catch (err) {
      setFormMsg((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const pending = (users || []).filter((u) => u.status === 'pending').length;

  return (
    <div
      class="om-auth-gate"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div class="om-auth-card om-admin-card">
        <p class="om-auth-kicker">Oculus-Clipeus - Accounts</p>
        <h2>Members{pending ? ` - ${pending} pending` : ''}</h2>
        <p>Approve pending sign-ups, reset a member's password, revoke / reinstate access, or provision a new member. Owner only.</p>
        {error && <div class="om-auth-error">{error}</div>}
        {users === null ? (
          <p class="om-auth-splash">Loading members...</p>
        ) : users.length === 0 ? (
          <p class="om-auth-hint">No member accounts yet. New users appear here after they sign up, or add one below.</p>
        ) : (
          <div class="om-admin-list">
            {users.map((u) => (
              <div class="om-admin-row" key={u.username}>
                <span class="om-admin-user">
                  <strong>{u.username}</strong> <span class="om-auth-hint">{u.role}{u.mustChangePassword ? ' - must change pw' : ''}</span>
                </span>
                <span class={`om-admin-status om-admin-${u.status}`}>{u.status}</span>
                <span class="om-admin-actions">
                  {u.status === 'pending' && (
                    <button class="om-auth-primary" type="button" disabled={busy === u.username} onClick={() => act(u.username, (x) => dataHub.approveUser(x))}>
                      {busy === u.username ? '...' : 'Approve'}
                    </button>
                  )}
                  {u.status === 'active' && u.role !== 'owner' && (
                    <button class="om-auth-secondary" type="button" onClick={() => { setMode(u.username); setFormPass(''); setFormMsg(null); }}>
                      Reset pw
                    </button>
                  )}
                  {u.status === 'active' && u.role !== 'owner' && (
                    <button class="om-auth-secondary" type="button" disabled={busy === u.username} onClick={() => act(u.username, (x) => dataHub.revokeUser(x))}>
                      {busy === u.username ? '...' : 'Revoke'}
                    </button>
                  )}
                  {u.status === 'revoked' && (
                    <button class="om-auth-secondary" type="button" disabled={busy === u.username} onClick={() => act(u.username, (x) => dataHub.reinstateUser(x))}>
                      {busy === u.username ? '...' : 'Reinstate'}
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        <form class="om-admin-form" onSubmit={submitForm}>
          <p class="om-auth-kicker" style="margin:2px 0 0">{mode === 'create' ? 'Add a member' : `Reset password - ${mode}`}</p>
          {mode === 'create' && (
            <input
              type="text"
              autocomplete="off"
              autocapitalize="none"
              placeholder="username (3-32: a-z 0-9 . _ -)"
              value={formUser}
              onInput={(e) => setFormUser((e.target as HTMLInputElement).value)}
            />
          )}
          <input
            type="password"
            autocomplete="new-password"
            placeholder={`temporary password (>= ${MIN_PW} chars)`}
            value={formPass}
            onInput={(e) => setFormPass((e.target as HTMLInputElement).value)}
          />
          {formMsg && <div class="om-auth-error">{formMsg}</div>}
          <div class="om-admin-form-actions">
            <button class="om-auth-primary" type="submit" disabled={busy === '__form'}>
              {busy === '__form' ? '...' : mode === 'create' ? 'Create member' : 'Set password'}
            </button>
            {mode !== 'create' && (
              <button class="om-auth-secondary" type="button" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
          <p class="om-auth-hint">
            {mode === 'create'
              ? 'New members start active but must change this temp password on first sign-in.'
              : 'The member must change this temp password on next sign-in.'}
          </p>
        </form>

        <div class="om-auth-actions">
          <button class="om-auth-secondary" type="button" onClick={load} disabled={users === null}>
            Refresh
          </button>
          <button class="om-auth-primary" type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
